import { CONVERSATIONAL_MM_AGENT } from '@fruitful/llm-service-shared';
import { useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    BracesIcon,
    ExternalLinkIcon,
    H6Emphasized,
    IconButton,
    ScrollTextIcon,
    ScrollView,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import { logRocketSessionsUrl } from '../../../common/utils/LinkUtils';
import { AiThreadTranscriptPanel } from '../../../llm-service/components/AiThreadTranscriptPanel';
import { ConversationalMMArtifact } from '../../../llm-service/conversational-mm/ConversationalMMArtifact';
import { useGetMembershipConversationalMMThread } from '../hooks/useGetMembershipMastraThread';

interface ConversationalMMSectionProps {
    membershipId: string;
}

/**
 * Conversational Money Map tab: chat transcript on the left, results artifact
 * on the right, plus a discreet modal that dumps the full
 * `mastra_threads.results` JSON.
 */
export const ConversationalMMSection = ({
    membershipId,
}: ConversationalMMSectionProps) => {
    const [isResultsJsonOpen, setIsResultsJsonOpen] = useState(false);

    const { data: thread, isLoading: isLoadingThread } =
        useGetMembershipConversationalMMThread({ membershipId });

    if (isLoadingThread) {
        return (
            <XStack justifyContent="center" padding="$4">
                <Spinner />
            </XStack>
        );
    }

    if (thread == null) {
        return (
            <YStack
                gap={8}
                padding={16}
                borderWidth={1}
                borderColor="$gray300"
                borderRadius={16}
                backgroundColor="$white"
            >
                <H6Emphasized>Conversational Money Map</H6Emphasized>
                <BodyMediumRegular color="$gray900">
                    No conversational money map thread found for this
                    membership.
                </BodyMediumRegular>
            </YStack>
        );
    }

    const resultsJson =
        thread.results != null
            ? JSON.stringify(thread.results, null, 2)
            : 'null';

    return (
        <YStack
            gap={12}
            padding={16}
            borderWidth={1}
            borderColor="$gray300"
            borderRadius={16}
            backgroundColor="$white"
        >
            <XStack justifyContent="space-between" alignItems="center">
                <YStack gap={4}>
                    <H6Emphasized>Conversational Money Map</H6Emphasized>
                    <BodySmallRegular color="$gray900">
                        Thread {thread.mastraThreadId} · Updated{' '}
                        {new Date(thread.updatedAt).toLocaleString()}
                    </BodySmallRegular>
                </YStack>
                <XStack alignItems="center" gap={4}>
                    {thread.results != null && (
                        <IconButton
                            icon={BracesIcon}
                            variant="tertiary"
                            size="tiny"
                            onPress={() => {
                                setIsResultsJsonOpen(true);
                            }}
                        />
                    )}
                    <IconButton
                        icon={ScrollTextIcon}
                        variant="tertiary"
                        size="tiny"
                        onPress={() => {
                            window.open(
                                `/ai/threads/${thread.mastraThreadId}?agentId=${CONVERSATIONAL_MM_AGENT.id}`,
                                '_blank',
                            );
                        }}
                    />
                    {(thread.visitorId != null || thread.userId != null) && (
                        <IconButton
                            icon={ExternalLinkIcon}
                            variant="tertiary"
                            size="tiny"
                            onPress={() => {
                                // CMM signup runs as a visitor on web; fall back
                                // to userId if the thread was later linked.
                                const logRocketUserId =
                                    thread.visitorId ?? thread.userId ?? '';
                                window.open(
                                    logRocketSessionsUrl({
                                        userId: logRocketUserId,
                                        platform: 'web',
                                    }),
                                    '_blank',
                                );
                            }}
                        />
                    )}
                </XStack>
            </XStack>

            <XStack flex={1} gap={16} minHeight="60vh" width="100%">
                <YStack
                    flex={1}
                    flexBasis={0}
                    width="50%"
                    minWidth={0}
                    minHeight={0}
                    borderWidth={1}
                    borderColor="$gray300"
                    borderRadius={12}
                    overflow="hidden"
                >
                    <XStack
                        paddingHorizontal={12}
                        paddingVertical={8}
                        borderBottomWidth={1}
                        borderBottomColor="$gray300"
                        backgroundColor="$gray100"
                    >
                        <BodySmallRegular fontWeight="700" color="$gray1100">
                            Conversation
                        </BodySmallRegular>
                    </XStack>
                    <AiThreadTranscriptPanel
                        threadId={thread.mastraThreadId}
                        agentId={CONVERSATIONAL_MM_AGENT.id}
                    />
                </YStack>

                <YStack
                    alignSelf="flex-start"
                    width="50%"
                    minWidth={0}
                    borderWidth={1}
                    borderColor="$gray300"
                    borderRadius={12}
                    overflow="hidden"
                >
                    <XStack
                        paddingHorizontal={12}
                        paddingVertical={8}
                        borderBottomWidth={1}
                        borderBottomColor="$gray300"
                        backgroundColor="$gray100"
                    >
                        <BodySmallRegular fontWeight="700" color="$gray1100">
                            Artifact
                        </BodySmallRegular>
                    </XStack>
                    {thread.results != null ? (
                        <ConversationalMMArtifact results={thread.results} />
                    ) : (
                        <YStack padding={16}>
                            <BodyMediumRegular color="$gray900">
                                This thread has no saved results yet.
                            </BodyMediumRegular>
                        </YStack>
                    )}
                </YStack>
            </XStack>

            <GroveSimpleModal
                isOpen={isResultsJsonOpen}
                onClose={() => {
                    setIsResultsJsonOpen(false);
                }}
                hasCloseButton
            >
                <YStack gap={12} minWidth={560} maxWidth={900} padding={8}>
                    <H6Emphasized>mastra_threads.results</H6Emphasized>
                    <BodySmallRegular color="$gray900">
                        Full results payload persisted on this thread.
                    </BodySmallRegular>
                    <ScrollView maxHeight="70vh">
                        <BodySmallRegular
                            color="$gray1200"
                            selectable
                            style={{
                                fontFamily: 'monospace',
                                fontSize: 12,
                                lineHeight: 18,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}
                        >
                            {resultsJson}
                        </BodySmallRegular>
                    </ScrollView>
                </YStack>
            </GroveSimpleModal>
        </YStack>
    );
};
